<template>
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger as-child>
        <Button
          type="button"
          @click="handleSave"
          :disabled="isDisabled"
          class="tw-gap-2 !tw-bg-secondary-500 tw-text-white !tw-border !tw-border-gray-300 hover:!tw-border-gray-400"
        >
          <template v-if="saveStatus === 'saving'">
            <div
              class="tw-w-4 tw-h-4 tw-border-2 tw-border-current tw-border-t-transparent tw-rounded-full tw-animate-spin"
            />
            Saving...
          </template>
          <template v-else-if="saveStatus === 'saved'">
            <CheckCircle class="tw-w-4 tw-h-4" />
            Saved
          </template>
          <template v-else-if="saveStatus === 'error'">
            <AlertCircle class="tw-w-4 tw-h-4" />
            Error
          </template>
          <template v-else>
            <Save class="tw-w-4 tw-h-4" />
            {{ t('common.forms.save') }} {{ t(sectionName) }}
          </template>
        </Button>
      </TooltipTrigger>
      <TooltipContent v-if="archivedDisabled">
        {{ t('common.message.customer_archived_tooltip') }}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useCustomerStore } from '@/stores/customer.store';
import { AlertCircle, CheckCircle, Save } from 'lucide-vue-next';
import { computed, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toast-notification';

const toast = useToast();
const { t } = useI18n();
const customerStore = useCustomerStore();

// Types
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type SaveResult = { success: boolean; message: string };

interface SectionSaveButtonProps {
  onSave: () => SaveResult | Promise<SaveResult>;
  isEdit: boolean;
  sectionName: string;
  disabled?: boolean;
}

// Props
const props = withDefaults(defineProps<SectionSaveButtonProps>(), {
  disabled: false,
});

// State
const saveStatus = ref<SaveStatus>('idle');
const timeoutRef = ref<number | null>(null);

const archivedDisabled = computed(() => customerStore.shouldDisableCustomerActions() === true);
const isDisabled = computed(
  () => archivedDisabled.value || props.disabled || saveStatus.value === 'saving' || !props.isEdit
);

// Cleanup timeout on unmount
onUnmounted(() => {
  if (timeoutRef.value) {
    clearTimeout(timeoutRef.value);
  }
});

// Methods
const handleSave = async () => {
  if (!props.isEdit) {
    toast.info('Complete the form to create the customer');
    return;
  }

  // Clear any existing timeout
  if (timeoutRef.value) {
    clearTimeout(timeoutRef.value);
  }

  saveStatus.value = 'saving';

  try {
    const result = await props.onSave();

    if (result.success) {
      saveStatus.value = 'saved';
      toast.success(`${t(props.sectionName)} saved successfully`);

      // Reset to idle after 2 seconds
      timeoutRef.value = window.setTimeout(() => (saveStatus.value = 'idle'), 2000);
    } else {
      saveStatus.value = 'error';
      toast.error(result.message || `Failed to save ${t(props.sectionName).toLowerCase()}`);

      // Reset to idle after 3 seconds
      timeoutRef.value = window.setTimeout(() => (saveStatus.value = 'idle'), 3000);
    }
  } catch (error) {
    saveStatus.value = 'error';
    toast.error(`Failed to save ${t(props.sectionName).toLowerCase()}`);
    timeoutRef.value = window.setTimeout(() => (saveStatus.value = 'idle'), 3000);
  }
};

const getButtonVariant = computed(() => {
  switch (saveStatus.value) {
    case 'saved':
      return 'default';
    case 'error':
      return 'destructive';
    default:
      return 'default';
  }
});
</script>
